import { motion } from "framer-motion";
import { Flower2, Users, Sun } from "lucide-react";
import { WaitlistForm } from "@/components/WaitlistForm";

const HERO_IMG = "/hero-retreat-group.png";

const BENEFITS = [
  { icon: Flower2, text: "Relax in a beautiful setting" },
  { icon: Users, text: "Connect with like-minded women" },
  { icon: Sun, text: "Feel supported, inspired and renewed" },
];

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: 0 },
  transition: { duration: 1, delay: 0.3 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
});

const rise = (delay) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => (
  <header data-testid="hero-section" className="relative grid min-h-screen lg:grid-cols-2">
    <div className="relative h-[46vh] overflow-hidden sm:h-[54vh] lg:h-auto lg:min-h-screen">
      <img
        src={HERO_IMG}
        alt="Women laughing together over drinks on a coastal terrace at sunset"
        className="hero-drift absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent_55%,rgba(249,243,238,0.4)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(249,243,238,0.55),transparent_30%)] lg:bg-none" />
    </div>

    <div className="relative flex items-center overflow-hidden bg-[#F9F3EE] px-6 py-14 sm:px-12 lg:py-20">
      <div className="pointer-events-none absolute -left-16 top-8 h-72 w-72 rounded-full bg-[#F3D9DC]/60 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-20 -right-10 h-80 w-80 rounded-full bg-[#F1E4D3]/70 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-lg lg:ml-8 xl:ml-14">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="retreat-name-title"
          className="mb-6 font-serif text-2xl italic text-[#A34A6B] sm:text-3xl"
        >
          The Menopause Reclaim Retreat
        </motion.p>
        <h1 className="font-serif text-4xl leading-[1.08] text-cream sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineAnim(0)}>
              Menopause is to be
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block italic text-[#A34A6B]" {...lineAnim(1)}>
              celebrated.
            </motion.span>
          </span>
        </h1>

        <motion.p {...rise(0.65)} className="mt-6 max-w-md font-serif text-2xl italic text-cream sm:text-3xl">
          Find the true woman within.
        </motion.p>

        <motion.div {...rise(0.78)} className="mt-6 max-w-md space-y-4 text-sm leading-relaxed text-lilac">
          <p className="font-semibold text-cream">
            A premium retreat experience for women ready to put themselves back on the agenda.
          </p>
          <p>
            You spend so much of your life showing up for others: leading teams, building businesses, managing
            responsibilities and meeting expectations. But during menopause and midlife, you may find yourself
            asking a different question:
          </p>
          <p className="border-l-2 border-[#A34A6B]/40 pl-4 font-serif text-lg italic text-[#A34A6B]">
            &ldquo;What about me?&rdquo;
          </p>
          <p>
            The Menopause Reclaim Retreat is your opportunity to step away from the demands of everyday life
            and finally focus on you.
          </p>
          <p>
            If you're struggling with brain fog, constant fatigue, disrupted sleep, anxiety, low confidence or
            feeling unlike yourself, you don't have to keep pushing through without understanding what's
            happening.
          </p>
          <p>
            This is more than a relaxing break. It's a practical, supportive experience designed to help you
            take control of your menopause journey.
          </p>
          <p className="font-semibold text-cream">During the retreat, you will:</p>
          <ul className="space-y-3">
            {[
              ["Understand your symptoms:", "Learn what's happening to your body and why symptoms such as brain fog, fatigue, mood changes and sleep disruption may be affecting you."],
              ["Discover practical solutions:", "Explore realistic strategies to support your sleep, energy, nutrition, stress levels and everyday wellbeing."],
              ["Rebuild your confidence:", "Develop techniques to manage the impact menopause can have on your self-esteem, relationships and professional life."],
              ["Create your personal menopause action plan:", "Identify your priorities, recognise your triggers and leave with practical steps you can implement at home and work."],
              ["Build your support network:", "Connect with like-minded women who understand what you're experiencing, so you no longer feel you have to navigate menopause alone."],
            ].map(([lead, rest]) => (
              <li key={lead} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#A34A6B]" aria-hidden="true" />
                <span>
                  <strong className="font-semibold text-cream">{lead}</strong> {rest}
                </span>
              </li>
            ))}
          </ul>
          <p>
            Using our Understand → Reset → Reclaim approach, you'll leave with greater knowledge of your body,
            practical tools to support your wellbeing, a personalised plan for moving forward and renewed
            confidence to embrace your next chapter.
          </p>
          <p className="font-serif text-base italic text-[#A34A6B]">
            You won't just leave feeling relaxed. You'll leave knowing what to do next.
          </p>
        </motion.div>

        <motion.ul {...rise(0.8)} className="mt-10 grid grid-cols-3 gap-4">
          {BENEFITS.map((b) => (
            <li key={b.text} data-testid={`benefit-${b.text.split(" ")[1].toLowerCase()}`} className="flex flex-col items-start gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F3D9DC]">
                <b.icon className="h-5 w-5 text-[#A34A6B]" />
              </span>
              <span className="text-xs font-medium leading-snug text-cream/90">{b.text}</span>
            </li>
          ))}
        </motion.ul>

        <motion.div {...rise(0.95)} className="mt-12 border-t border-[rgba(163,74,107,0.15)] pt-10">
          <p className="mb-4 font-serif text-lg italic leading-snug text-[#A34A6B]">
            Reserve your place towards understanding, reclaiming and resetting the woman within
          </p>
          <h2 className="font-serif text-2xl leading-snug text-cream sm:text-3xl">
            Make space for the woman you are <span className="italic text-[#A34A6B]">becoming.</span>
          </h2>
          <div className="mt-8 rounded-3xl border border-[rgba(163,74,107,0.18)] bg-white/85 p-6 shadow-[0_24px_70px_rgba(163,74,107,0.12)] backdrop-blur-sm sm:p-8">
            <WaitlistForm idPrefix="hero-waitlist" />
          </div>
        </motion.div>
      </div>
    </div>
  </header>
);
